import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { User, Building2, ClipboardList, BarChart3, Phone, PhoneOff, Tag, Plus } from "lucide-react";
import { format } from "date-fns";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import UserFunnelTimeline from "./UserFunnelTimeline";

export interface AdminUser {
  user_id: string;
  full_name: string | null;
  email: string | null;
  lead_source: string | null;
  company_name: string | null;
  subscription_tier: string;
  company_size: number;
  email_confirmed_at: string | null;
  last_sign_in_at: string | null;
  created_at: string;
  survey: {
    car_count: string;
    biggest_headache: string;
    open_to_call: boolean;
  } | null;
  usage: {
    vehicles: number;
    expenses: number;
    trips: number;
    maintenance: number;
    recurring_expenses: number;
    loans: number;
    ai_chats: number;
  };
}

const tierColor: Record<string, string> = {
  free: "bg-muted text-muted-foreground",
  pro: "bg-primary/15 text-primary",
  scale: "bg-accent text-accent-foreground",
};

const LEAD_SOURCE_OPTIONS = [
  "Car Rental Coach",
  "Organic",
  "Reddit",
  "Twitter/X",
  "Facebook",
  "Turo Forum",
  "Referral",
  "Google Ads",
  "YouTube",
  "TikTok",
];

const sourceColor: Record<string, string> = {
  "Car Rental Coach": "border-amber-400 text-amber-600",
  Reddit: "border-orange-400 text-orange-600",
  "Twitter/X": "border-sky-400 text-sky-600",
  Facebook: "border-blue-400 text-blue-600",
  "Google Ads": "border-emerald-400 text-emerald-600",
  YouTube: "border-red-400 text-red-600",
  TikTok: "border-pink-400 text-pink-600",
  Referral: "border-violet-400 text-violet-600",
  "Turo Forum": "border-teal-400 text-teal-600",
  Organic: "border-muted-foreground/40 text-muted-foreground",
};

function LeadSourcePill({ user }: { user: AdminUser }) {
  const [open, setOpen] = useState(false);
  const [customValue, setCustomValue] = useState("");
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();

  const save = async (value: string | null) => {
    setSaving(true);
    setOpen(false);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) return;
      await supabase.functions.invoke("admin-update-lead-source", {
        body: { target_user_id: user.user_id, lead_source: value },
      });
      queryClient.setQueryData<AdminUser[]>(["admin-users"], (old) =>
        old?.map((u) => (u.user_id === user.user_id ? { ...u, lead_source: value } : u))
      );
    } finally {
      setSaving(false);
      setCustomValue("");
    }
  };

  const colorClass = user.lead_source
    ? sourceColor[user.lead_source] || "border-primary/40 text-primary"
    : "";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {user.lead_source ? (
          <button
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors hover:bg-muted/50 ${colorClass} ${saving ? "opacity-50" : ""}`}
          >
            <Tag className="h-3 w-3" />
            {user.lead_source}
          </button>
        ) : (
          <button
            className={`inline-flex items-center gap-1 rounded-full border border-dashed border-muted-foreground/30 px-2 py-0.5 text-[11px] text-muted-foreground/60 transition-colors hover:border-muted-foreground/50 hover:text-muted-foreground ${saving ? "opacity-50" : ""}`}
          >
            <Plus className="h-3 w-3" />
            Source
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-48 p-2" align="start">
        <div className="space-y-1">
          {LEAD_SOURCE_OPTIONS.map((opt) => (
            <button
              key={opt}
              onClick={() => save(opt)}
              className={`w-full rounded px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted ${
                user.lead_source === opt ? "bg-muted font-medium" : ""
              }`}
            >
              {opt}
            </button>
          ))}
          <div className="border-t pt-1">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (customValue.trim()) save(customValue.trim());
              }}
            >
              <Input
                placeholder="Custom…"
                value={customValue}
                onChange={(e) => setCustomValue(e.target.value)}
                className="h-7 text-sm"
              />
            </form>
          </div>
          {user.lead_source && (
            <button
              onClick={() => save(null)}
              className="w-full rounded px-2 py-1.5 text-left text-sm text-destructive transition-colors hover:bg-destructive/10"
            >
              Remove
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function AdminUserCard({ user }: { user: AdminUser }) {
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        {/* Account */}
        <div className="border-b p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 shrink-0 text-muted-foreground" />
                <Link to={`/admin/users/${user.user_id}`} className="truncate font-medium text-primary hover:underline">
                  {user.full_name || "—"}
                </Link>
              </div>
              <p className="mt-0.5 truncate text-sm text-muted-foreground">{user.email || "—"}</p>
            </div>
            <Badge variant="secondary" className={tierColor[user.subscription_tier] || tierColor.free}>
              {user.subscription_tier || "free"}
            </Badge>
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Building2 className="h-3.5 w-3.5" />
              {user.company_name || "No company"}
            </span>
            <span>·</span>
            <span>{user.company_size} member{user.company_size !== 1 ? "s" : ""}</span>
            {user.created_at && (
              <>
                <span>·</span>
                <span>Signed up {format(new Date(user.created_at), "MMM d, yyyy")}</span>
              </>
            )}
            {user.last_sign_in_at && (
              <>
                <span>·</span>
                <span>Last in {format(new Date(user.last_sign_in_at), "MMM d, yyyy")}</span>
              </>
            )}
          </div>
          {/* Lead source + Funnel */}
          <div className="mt-2 flex items-center gap-2">
            <LeadSourcePill user={user} />
          </div>
          <div className="mt-3">
            <UserFunnelTimeline user={user} />
          </div>
        </div>

        {/* Survey */}
        <div className="border-b p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <ClipboardList className="h-3.5 w-3.5" />
              Onboarding Survey
            </h4>
          </div>
          {user.survey ? (
            <div className="space-y-1 text-sm">
              <p>
                <span className="text-muted-foreground">Cars:</span> {user.survey.car_count}
              </p>
              <p>
                <span className="text-muted-foreground">Challenge:</span> {user.survey.biggest_headache}
              </p>
              <p className="flex items-center gap-1">
                {user.survey.open_to_call ? (
                  <>
                    <Phone className="h-3.5 w-3.5 text-primary" /> Open to call
                  </>
                ) : (
                  <>
                    <PhoneOff className="h-3.5 w-3.5 text-muted-foreground" /> Not open to call
                  </>
                )}
              </p>
            </div>
          ) : (
            <p className="text-sm italic text-muted-foreground">No survey response</p>
          )}
        </div>

        {/* Usage */}
        <div className="p-4">
          <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <BarChart3 className="h-3.5 w-3.5" />
            Product Usage
          </h4>
          <div className="grid grid-cols-4 gap-2 text-center">
            <div>
              <p className="text-lg font-semibold">{user.usage.vehicles}</p>
              <p className="text-xs text-muted-foreground">Vehicles</p>
            </div>
            <div>
              <p className="text-lg font-semibold">{user.usage.expenses}</p>
              <p className="text-xs text-muted-foreground">Expenses</p>
            </div>
            <div>
              <p className="text-lg font-semibold">{user.usage.trips}</p>
              <p className="text-xs text-muted-foreground">Earnings</p>
            </div>
            <div>
              <p className="text-lg font-semibold">{user.usage.maintenance}</p>
              <p className="text-xs text-muted-foreground">Maint.</p>
            </div>
            <div>
              <p className="text-lg font-semibold">{user.usage.recurring_expenses}</p>
              <p className="text-xs text-muted-foreground">Recurring</p>
            </div>
            <div>
              <p className="text-lg font-semibold">{user.usage.loans}</p>
              <p className="text-xs text-muted-foreground">Loans</p>
            </div>
            <div>
              <p className="text-lg font-semibold">{user.usage.ai_chats}</p>
              <p className="text-xs text-muted-foreground">AI Chats</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
